import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Mail, CheckCircle2 } from 'lucide-react';
import {
  passwordResetRequestSchema,
  type PasswordResetRequestInput,
} from '@/shared/validation/auth';
import { api } from '@/client/lib/api';
import { Button } from '@/client/components/ui/button';
import { Input } from '@/client/components/ui/input';
import { Label } from '@/client/components/ui/label';
import { isLocale, type Locale } from '@/client/i18n';

export function ForgotPassword() {
  const { t } = useTranslation();
  const params = useParams();
  const locale: Locale = isLocale(params.locale) ? params.locale : 'sk';
  const [done, setDone] = useState(false);

  const form = useForm<PasswordResetRequestInput>({
    resolver: zodResolver(passwordResetRequestSchema),
    defaultValues: { email: '' },
  });

  async function onSubmit(values: PasswordResetRequestInput) {
    await api('/api/v1/auth/password/reset/request', { method: 'POST', body: values });
    setDone(true);
  }

  return (
    <div className="relative">
      <div className="mx-auto max-w-md px-5 sm:px-6 py-12 sm:py-20">
        <div className="dr-card-strong p-8 sm:p-10 animate-fade-up">
          {done ? (
            <div className="text-center">
              <div className="relative mx-auto h-20 w-20 mb-6">
                <div
                  className="absolute inset-0 rounded-full"
                  style={{
                    background:
                      'conic-gradient(from 220deg, rgba(45,139,85,0.30), rgba(193,167,226,0.30), rgba(45,139,85,0.30))',
                    filter: 'blur(8px)',
                  }}
                  aria-hidden
                />
                <div className="relative h-full w-full rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/15 flex items-center justify-center animate-float">
                  <CheckCircle2 size={36} className="text-success" />
                </div>
              </div>
              <p className="dr-eyebrow">
                {t('auth.forgot_done_eyebrow', { defaultValue: 'Odoslané' })}
              </p>
              <h1 className="mt-3 font-display text-h2 text-primary-dark">
                {t('auth.forgot_title')}
              </h1>
              <p className="mt-3 font-sans text-body text-text-secondary leading-relaxed">
                {t('auth.forgot_success')}
              </p>
              <Link
                to={`/${locale}/prihlasenie`}
                className="mt-8 inline-flex items-center gap-2 font-sans text-body-small text-primary hover:gap-3 transition-all"
              >
                <ArrowLeft size={14} />
                {t('nav.login')}
              </Link>
            </div>
          ) : (
            <>
              <div className="text-center">
                <div className="mx-auto h-14 w-14 mb-5 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/15 flex items-center justify-center">
                  <Mail size={24} className="text-primary" />
                </div>
                <p className="dr-eyebrow">
                  {t('auth.forgot_eyebrow', { defaultValue: 'Pomoc s heslom' })}
                </p>
                <h1 className="mt-3 font-display text-h2 text-primary-dark">
                  {t('auth.forgot_title')}
                </h1>
                <p className="mt-2 font-sans text-body text-text-secondary">
                  {t('auth.forgot_subtitle')}
                </p>
              </div>

              <form onSubmit={form.handleSubmit(onSubmit)} className="mt-8 space-y-4" noValidate>
                <div>
                  <Label htmlFor="email">{t('auth.email_label')}</Label>
                  <Input id="email" type="email" autoComplete="email" {...form.register('email')} />
                  {form.formState.errors.email && (
                    <p className="mt-1.5 text-caption text-error">
                      {form.formState.errors.email.message}
                    </p>
                  )}
                </div>
                <Button
                  type="submit"
                  className="w-full mt-2"
                  size="lg"
                  disabled={form.formState.isSubmitting}
                >
                  {form.formState.isSubmitting ? t('common.loading') : t('auth.forgot_submit')}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <Link
                  to={`/${locale}/prihlasenie`}
                  className="inline-flex items-center gap-2 font-sans text-body-small text-primary hover:gap-3 transition-all"
                >
                  <ArrowLeft size={14} />
                  {t('nav.login')}
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
